import React, { lazy, Suspense, useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { BrandedRouteFallback } from "./components/common/BrandedRouteFallback";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const  DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPage"));
const PageHelp = lazy(() => import("./components/help/PageHelp").then((module) => ({ default: module.PageHelp })));
const ProtectedRoute = lazy(() => import("./components/routing/ProtectedRoute"));
const AdminRoute = lazy(() => import("./components/routing/AdminRoute"));
const RoleRoute = lazy(() => import("./components/routing/RoleRoute"));
const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
// AdminRoleApprovalPinsPage removed (auth-based approvals).
const AdminAuditLogsPage = lazy(() => import("./pages/admin/AdminAuditLogsPage"));
const AdminMemberApplicationsPage = lazy(() => import("./pages/admin/AdminMemberApplicationsPage"));
const AdminMemberApplicationDetailsPage = lazy(() => import("./pages/admin/AdminMemberApplicationDetailsPage"));
const AdminOnboardingPage = lazy(() => import("./pages/admin/AdminOnboardingPage"));
const AdminPartnersPage = lazy(() => import("./pages/admin/AdminPartnersPage"));
const AdminPartnerDetailsPage = lazy(() => import("./pages/admin/AdminPartnerDetailsPage"));
const AdminLinksPage = lazy(() => import("./pages/admin/AdminLinksPage"));
const AdminContactsPage = lazy(() => import("./pages/admin/AdminContactsPage"));
const AdminNewsPage = lazy(() => import("./pages/admin/AdminNewsPage"));
const AdminAboutPage = lazy(() => import("./pages/admin/AdminAboutPage"));
const AdminLegalPage = lazy(() => import("./pages/admin/AdminLegalPage"));
const AdminEventsPage = lazy(() => import("./pages/admin/AdminEventsPage"));
const AdminAnnouncementsPage = lazy(() => import("./pages/admin/AdminAnnouncementsPage"));
const AnnouncementsPage = lazy(() => import("./pages/AnnouncementsPage"));
const AdminStationsPage = lazy(() => import("./pages/admin/AdminStationsPage"));
const AdminLocationSettingsPage = lazy(() => import("./pages/admin/AdminLocationSettingsPage"));
const AdminComplianceTypesPage = lazy(() => import("./pages/admin/AdminComplianceTypesPage"));
const AdminStationFeesPage = lazy(() => import("./pages/admin/AdminStationFeesPage"));
const AdminMembersPage = lazy(() => import("./pages/admin/AdminMembersPage"));
const AdminCertificatePreparePage = lazy(() => import("./pages/admin/AdminCertificatePreparePage"));
const LeadershipCertificateExportPage = lazy(() =>
  import("./pages/LeadershipCertificateExportPage")
);
const ExportedCertificatesPage = lazy(() =>
  import("./pages/ExportedCertificatesPage")
);
const AdminInvoicesPage = lazy(() => import("./pages/admin/AdminInvoicesPage"));
const AdminPaymentProcessingPage = lazy(() => import("./pages/admin/AdminPaymentProcessingPage"));
const AdminReportsMembersPage = lazy(() => import("./pages/admin/AdminReportsMembersPage"));
const AdminReportsFinancePage = lazy(() => import("./pages/admin/AdminReportsFinancePage"));
const MemberApplicationPage = lazy(() => import("./pages/member/MemberApplicationPage"));
const MemberProfilePage = lazy(() => import("./pages/member/MemberProfilePage"));
const MemberDashboardPage = lazy(() => import("./pages/member/MemberDashboardPage"));
const MemberStationsPage = lazy(() => import("./pages/member/MemberStationsPage"));
const MemberBillingStatusPage = lazy(() => import("./pages/member/MemberBillingStatusPage"));
const MemberInvoicesPage = lazy(() => import("./pages/member/MemberInvoicesPage"));
const MemberPaymentsPage = lazy(() => import("./pages/member/MemberPaymentsPage"));
const ComplianceDocumentsPage = lazy(() => import("./pages/member/ComplianceDocumentsPage"));
const ComplianceUploadPage = lazy(() => import("./pages/member/ComplianceUploadPage"));
const ComplianceRemindersPage = lazy(() => import("./pages/member/ComplianceRemindersPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const MemberOilPricesPage = lazy(() => import("./pages/member/MemberOilPricesPage"));
const StationMembershipStatusPage = lazy(() => import("./pages/member/StationMembershipStatusPage"));
const StationPaymentsPage = lazy(() => import("./pages/member/StationPaymentsPage"));
const StationDetailPage = lazy(() => import("./pages/member/StationDetailPage"));
const AdminOilPricesPage = lazy(() => import("./pages/admin/AdminOilPricesPage"));
const AdminStationPaymentsPage = lazy(() => import("./pages/admin/AdminStationPaymentsPage"));
const AdminStationMembershipReportPage = lazy(() =>
  import("./pages/admin/AdminStationMembershipReportPage")
);

// Just for deployment 
export default function App() {
  const toastOptions = useMemo(() => ({ duration: 5000 }), []);
  return (
    <>
    <Toaster position="top-center" toastOptions={toastOptions} />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Suspense fallback={<BrandedRouteFallback variant="fullscreen" />}><LoginPage /></Suspense>} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/forgot-password" element={<Suspense fallback={<BrandedRouteFallback variant="fullscreen" />}><PlaceholderPage title="Forgot Password" description="Coming soon." /><PageHelp placement="fixed" /></Suspense>} />
      <Route
        path="/dashboard"
        element={
          <Suspense fallback={<BrandedRouteFallback variant="fullscreen" />}>
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          </Suspense>
        }
      >
        <Route index element={<DashboardPage />} />

        {/* Common authenticated pages (all roles) */}
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route
          path="events"
          element={<PlaceholderPage title="Events" description="Coming soon." />}
        />
        <Route
          path="notifications"
          element={<PlaceholderPage title="Notifications" description="Coming soon." />}
        />
        <Route path="profile" element={<ProfilePage />} />

        {/* Member (Owner/Operator) */}
        <Route
          path="member/dashboard"
          element={
            <RoleRoute allow={["member", "public", "chairman", "secretary"]}>
              <MemberDashboardPage />
            </RoleRoute>
          }
        />
        <Route
          path="member/application"
          element={
            <RoleRoute allow={["member", "public"]}>
              <MemberApplicationPage />
            </RoleRoute>
          }
        />
        <Route
          path="member/profile"
          element={
            <RoleRoute allow={["member"]}>
              <MemberProfilePage />
            </RoleRoute>
          }
        />
        <Route
          path="stations"
          element={
            <RoleRoute allow={["member"]}>
              <MemberStationsPage />
            </RoleRoute>
          }
        />
        <Route
          path="stations/:id"
          element={
            <RoleRoute allow={["member"]}>
              <StationDetailPage />
            </RoleRoute>
          }
        />
        <Route
          path="compliance/documents"
          element={
            <RoleRoute allow={["member", "station_manager"]}>
              <ComplianceDocumentsPage />
            </RoleRoute>
          }
        />
        <Route
          path="compliance/upload"
          element={
            <RoleRoute allow={["member", "station_manager"]}>
              <ComplianceUploadPage />
            </RoleRoute>
          }
        />
        <Route
          path="compliance/reminders"
          element={
            <RoleRoute allow={["member"]}>
              <ComplianceRemindersPage />
            </RoleRoute>
          }
        />
        <Route
          path="billing/status"
          element={
            <RoleRoute allow={["member"]}>
              <MemberBillingStatusPage />
            </RoleRoute>
          }
        />
        <Route
          path="billing/invoices"
          element={
            <RoleRoute allow={["member"]}>
              <MemberInvoicesPage />
            </RoleRoute>
          }
        />
        <Route
          path="billing/payments"
          element={
            <RoleRoute allow={["member"]}>
              <MemberPaymentsPage />
            </RoleRoute>
          }
        />
        <Route
          path="station-membership"
          element={
            <RoleRoute allow={["member"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <StationMembershipStatusPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="station-payments"
          element={
            <RoleRoute allow={["member"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <StationPaymentsPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="benefits/offers"
          element={
            <RoleRoute allow={["member"]}>
              <PlaceholderPage title="Partner Offers" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="benefits/inquiries"
          element={
            <RoleRoute allow={["member"]}>
              <PlaceholderPage title="My Inquiries" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="oil-prices"
          element={
            <RoleRoute allow={["member"]}>
              <MemberOilPricesPage />
            </RoleRoute>
          }
        />

        {/* Station manager */}
        <Route
          path="assigned-stations"
          element={
            <RoleRoute allow={["station_manager"]}>
              <PlaceholderPage title="Assigned Stations" description="Coming soon." />
            </RoleRoute>
          }
        />

        {/* Partner */}
        <Route
          path="partner/offers"
          element={
            <RoleRoute allow={["partner"]}>
              <PlaceholderPage title="My Offers" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="partner/offers/create"
          element={
            <RoleRoute allow={["partner"]}>
              <PlaceholderPage title="Create Offer" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="partner/offers/pipeline"
          element={
            <RoleRoute allow={["partner"]}>
              <PlaceholderPage title="Drafts / Pending Approval" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="partner/inquiries"
          element={
            <RoleRoute allow={["partner"]}>
              <PlaceholderPage title="Inquiries" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="partner/analytics"
          element={
            <RoleRoute allow={["partner"]}>
              <PlaceholderPage title="Analytics" description="Coming soon." />
            </RoleRoute>
          }
        />

        {/* Regulator/observer */}
        <Route
          path="regulator/compliance"
          element={
            <RoleRoute allow={["regulator"]}>
              <PlaceholderPage title="Compliance Overview" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="regulator/stations"
          element={
            <RoleRoute allow={["regulator"]}>
              <PlaceholderPage title="Stations" description="Coming soon." />
            </RoleRoute>
          }
        />
        <Route
          path="regulator/reports"
          element={
            <RoleRoute allow={["regulator"]}>
              <PlaceholderPage title="Reports / Exports" description="Coming soon." />
            </RoleRoute>
          }
        />

        <Route
          path="admin/users"
          element={
            <RoleRoute allow={["admin"]}>
              <AdminUsersPage />
            </RoleRoute>
          }
        />
        {/* admin/role-approval-pins removed */}
        <Route
          path="admin/audit-logs"
          element={
            <RoleRoute allow={["admin", "chairman", "secretary"]}>
              <AdminAuditLogsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/applications"
          element={
            <RoleRoute allow={["membership_manager", "admin"]}>
              <AdminMemberApplicationsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/applications/:id"
          element={
            <RoleRoute allow={["membership_manager", "admin"]}>
              <AdminMemberApplicationDetailsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/onboarding"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <AdminOnboardingPage />
              </Suspense>
            </RoleRoute>
          }
        />
        {/* Admin backend features (routes exist on server; UI wiring can follow) */}
        <Route
          path="admin/stations"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <AdminStationsPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="admin/compliance-types"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <AdminComplianceTypesPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="admin/location-settings"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminLocationSettingsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/station-fees"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminStationFeesPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/documents"
          element={
            <AdminRoute>
              <div className="rounded-xl border border-tapsoa-black/10 bg-tapsoa-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-tapsoa-red">Documents (Admin)</h2>
                <p className="mt-2 text-sm text-tapsoa-gray-dark">
                  Coming soon. Backend route: <code>/admin/documents</code>
                </p>
              </div>
            </AdminRoute>
          }
        />
        <Route
          path="admin/billing"
          element={
            <RoleRoute allow={["membership_manager", "chairman", "secretary"]}>
              <AdminInvoicesPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/reports"
          element={
            <AdminRoute>
              <div className="rounded-xl border border-tapsoa-black/10 bg-tapsoa-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-tapsoa-red">Reports (Admin)</h2>
                <p className="mt-2 text-sm text-tapsoa-gray-dark">
                  Coming soon. Backend route: <code>/admin/reports</code>
                </p>
              </div>
            </AdminRoute>
          }
        />
        <Route
          path="admin/announcements"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminAnnouncementsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/events"
          element={
            <AdminRoute>
              <AdminEventsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/offers"
          element={
            <AdminRoute>
              <div className="rounded-xl border border-tapsoa-black/10 bg-tapsoa-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-tapsoa-red">Offers (Admin)</h2>
                <p className="mt-2 text-sm text-tapsoa-gray-dark">
                  Coming soon. Backend route: <code>/admin/offers</code>
                </p>
              </div>
            </AdminRoute>
          }
        />
        <Route
          path="admin/partners"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminPartnersPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/partners/:id"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminPartnerDetailsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/links"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminLinksPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/news"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminNewsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/contacts"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminContactsPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/about"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminAboutPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/legal"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <AdminLegalPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/oil-prices"
          element={
            <RoleRoute allow={["admin", "membership_manager", "chairman", "secretary"]}>
              <AdminOilPricesPage />
            </RoleRoute>
          }
        />
        <Route
          path="admin/contact-forms"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <div className="rounded-xl border border-tapsoa-black/10 bg-tapsoa-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-tapsoa-red">Contact Forms (Admin)</h2>
                <p className="mt-2 text-sm text-tapsoa-gray-dark">
                  Coming soon. Backend route: <code>/admin/contact-forms</code>
                </p>
              </div>
            </RoleRoute>
          }
        />
        <Route
          path="admin/broadcast"
          element={
            <AdminRoute>
              <div className="rounded-xl border border-tapsoa-black/10 bg-tapsoa-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-tapsoa-red">Broadcast (Admin)</h2>
                <p className="mt-2 text-sm text-tapsoa-gray-dark">
                  Coming soon. Backend route: <code>/admin/broadcast</code>
                </p>
              </div>
            </AdminRoute>
          }
        />
        <Route
          path="admin/analytics/communications"
          element={
            <AdminRoute>
              <div className="rounded-xl border border-tapsoa-black/10 bg-tapsoa-white p-6 shadow-sm">
                <h2 className="text-xl font-bold text-tapsoa-red">
                  Communication Analytics (Admin)
                </h2>
                <p className="mt-2 text-sm text-tapsoa-gray-dark">
                  Coming soon. Backend route:{" "}
                  <code>/admin/analytics/communications</code>
                </p>
              </div>
            </AdminRoute>
          }
        />

        <Route
          path="admin/certificates"
          element={
            <AdminRoute>
              <Navigate to="/dashboard/admin/certificates/prepare" replace />
            </AdminRoute>
          }
        />
        <Route
          path="admin/certificates/prepare"
          element={
            <RoleRoute allow={["membership_manager"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <AdminCertificatePreparePage />
              </Suspense>
            </RoleRoute>
          }
        />

        <Route
          path="certificates/export"
          element={
            <RoleRoute allow={["chairman", "secretary"]}>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <LeadershipCertificateExportPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="certificates/exported"
          element={
            <RoleRoute
              allow={["admin", "membership_manager", "chairman", "secretary"]}
            >
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <ExportedCertificatesPage />
              </Suspense>
            </RoleRoute>
          }
        />
        {/* Additional admin placeholders to match the proposed menu */}
        <Route
          path="admin/members"
          element={
            <AdminRoute>
              <Suspense fallback={<BrandedRouteFallback variant="embedded" />}>
                <AdminMembersPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/members/applications"
          element={
            <AdminRoute>
              <PlaceholderPage title="Member Applications" description="Coming soon." />
            </AdminRoute>
          }
        />
        <Route
          path="admin/payments/processing"
          element={
            <AdminRoute>
              <AdminPaymentProcessingPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/station-payments"
          element={
            <AdminRoute>
              <AdminStationPaymentsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/reports/members"
          element={
            <AdminRoute>
              <AdminReportsMembersPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/reports/finance"
          element={
            <AdminRoute>
              <AdminReportsFinancePage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/reports/compliance"
          element={
            <AdminRoute>
              <PlaceholderPage title="Compliance Analytics (Admin)" description="Coming soon." />
            </AdminRoute>
          }
        />
        <Route
          path="admin/reports/usage"
          element={
            <AdminRoute>
              <PlaceholderPage title="Usage / Activity (Admin)" description="Coming soon." />
            </AdminRoute>
          }
        />
        <Route
          path="admin/reports/station-membership"
          element={
            <AdminRoute>
              <AdminStationMembershipReportPage />
            </AdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}
